import React, { useEffect, useState } from 'react';
import { useAsgardeoAuth } from '../../hooks/useAsgardeoAuth';
import AppRoutes from '../../routes';

function Authenticate() {
  const {
    isAuthenticated,
    isLoading,
    signIn,
    signOut,
    getUserInfo
  } = useAsgardeoAuth();

  const [userInfo, setUserInfo] = useState(null);
  const [isAllowed, setIsAllowed] = useState(null);
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [emailsLoaded, setEmailsLoaded] = useState(false);

  // Helper function to normalize email for comparison
  const normalizeEmail = (email) => {
    return email ? email.trim().toLowerCase() : '';
  };

  // Helper function to check if email is allowed
  const isEmailAllowed = (userEmail, allowedEmailsList) => {
    const normalizedUserEmail = normalizeEmail(userEmail);
    const normalizedAllowedEmails = allowedEmailsList.map(normalizeEmail);
    
    console.log('Email comparison:');
    console.log('  User email (normalized):', normalizedUserEmail);
    console.log('  Allowed emails (normalized):', normalizedAllowedEmails);
    
    const isAllowed = normalizedAllowedEmails.includes(normalizedUserEmail);
    console.log('  Is allowed:', isAllowed);
    
    return isAllowed;
  };

  // Load allowed emails
  useEffect(() => {
    console.log('Loading allowed emails...');
    fetch('/allowed_emails.json')
      .then(res => {
        console.log('Allowed emails response status:', res.status);
        return res.ok ? res.json() : [];
      })
      .then(emails => {
        console.log('Loaded allowed emails:', emails);
        setAllowedEmails(emails);
      })
      .catch(err => {
        console.error('Error loading allowed emails:', err);
        setAllowedEmails([]);
      })
      .finally(() => setEmailsLoaded(true));
  }, []);

  // Auto sign-in
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      signIn();
    }
  }, [isAuthenticated, isLoading, signIn]);

  // Remove URL params after auth redirect
  useEffect(() => {
    if (isAuthenticated) {
      const params = new URLSearchParams(window.location.search);
      if (params.has('code') || params.has('session_state')) {
        window.history.replaceState({}, '', '/');
      }
    }
  }, [isAuthenticated]);

  // Get user info and verify email
  useEffect(() => {
    if (isAuthenticated && !userInfo && emailsLoaded) {
      console.log('Getting user info...');
      getUserInfo()
        .then(info => {
          console.log('User info received:', info);
          setUserInfo(info);
          
          // Try to get email from different possible sources
          let userEmail = info.email;
          
          // Fallback methods to get email
          if (!userEmail) {
            userEmail = info.preferred_username || info.username || info.sub;
            console.log('Trying fallback email sources:', { 
              preferred_username: info.preferred_username, 
              username: info.username, 
              sub: info.sub 
            });
          }
          
          // Check if email exists in user info
          if (!userEmail) {
            console.error('No email found in user info');
            console.log('Full user info object:', JSON.stringify(info, null, 2));
            alert('Access denied: No email found in user profile');
            signOut();
            return;
          }
          
          console.log('User email:', userEmail);
          console.log('Allowed emails:', allowedEmails);
          
          const emailAllowed = isEmailAllowed(userEmail, allowedEmails);
          console.log('Email allowed:', emailAllowed);
          
          setIsAllowed(emailAllowed);
          
          if (!emailAllowed) {
            console.log('Access denied for email:', userEmail);
            alert(`Access denied: Email ${userEmail} is not authorized. Please contact administrator.`);
            signOut();
          } else {
            console.log('Access granted for email:', userEmail);
          }
        })
        .catch(err => {
          console.error('User info fetch failed:', err);
          alert('Failed to get user information');
          signOut();
        });
    }
  }, [isAuthenticated, userInfo, emailsLoaded, allowedEmails, getUserInfo, signOut]);

  if (isLoading || !isAuthenticated || isAllowed === null || !emailsLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-lg text-gray-700">Loading...</p>
        </div>
      </div>
    );
  }

  return <AppRoutes userInfo={userInfo} />;
}

export default Authenticate;
